import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MapPinIcon, ClockIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate API call
    setTimeout(() => {
      setFormStatus('success');
    }, 1500);
  };
  return <div className="w-full bg-white" data-id="element-154">
      {/* Header */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-155">
        <div className="max-w-7xl mx-auto text-center" data-id="element-156">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-4" data-id="element-157">
            Contact & <span className="text-brand-red" data-id="element-158">Get a Quote</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg text-steel-300 font-body max-w-2xl mx-auto" data-id="element-159">
            Fast estimates. Fast turnaround. Tell us about your project and
            we'll get back to you with a clear, no-nonsense quote.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4" data-id="element-160">
        <div className="max-w-7xl mx-auto" data-id="element-161">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-162">
            {/* Left Col: Contact Info */}
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="lg:col-span-4 space-y-8" data-id="element-163">
              <motion.div variants={fadeInUp} className="bg-steel-50 p-8 border border-steel-100" data-id="element-164">
                <h2 className="text-2xl font-heading font-bold uppercase text-charcoal mb-6" data-id="element-165">
                  Direct Contact
                </h2>
                <div className="space-y-6" data-id="element-166">
                  <a href="tel:5177778088" className="flex items-start group" data-id="element-167">
                    <PhoneIcon className="w-6 h-6 text-brand-red mt-1 mr-4 group-hover:scale-110 transition-transform" data-id="element-168" />
                    <div data-id="element-169">
                      <p className="font-bold text-charcoal text-lg group-hover:text-brand-red transition-colors" data-id="element-170">
                        (517) 777-8088
                      </p>
                      <p className="text-sm text-steel-500" data-id="element-171">
                        Primary Phone (Call for fastest response)
                      </p>
                    </div>
                  </a>
                  <div className="flex items-start" data-id="element-172">
                    <MapPinIcon className="w-6 h-6 text-brand-red mt-1 mr-4" data-id="element-173" />
                    <div data-id="element-174">
                      <p className="font-bold text-charcoal text-lg" data-id="element-175">
                        Fowlerville, MI
                      </p>
                      <p className="text-sm text-steel-500" data-id="element-176">
                        Serving the entire state of Michigan
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-177">
                    <ClockIcon className="w-6 h-6 text-brand-red mt-1 mr-4" data-id="element-178" />
                    <div data-id="element-179">
                      <p className="font-bold text-charcoal text-lg" data-id="element-180">
                        Business Hours
                      </p>
                      <p className="text-sm text-steel-500" data-id="element-181">
                        Mon-Fri: 8:00 AM - 6:00 PM
                        <br data-id="element-182" />
                        Sat: By Appointment
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-charcoal p-8 text-white" data-id="element-183">
                <h3 className="text-xl font-heading font-bold uppercase mb-4" data-id="element-184">
                  Mobile Service
                </h3>
                <p className="text-steel-300 text-sm leading-relaxed mb-6" data-id="element-185">
                  We are a fully mobile operation. We bring our dustless and dry
                  blasting equipment directly to your site, facility, or home
                  anywhere in Michigan.
                </p>
                <a href="https://www.facebook.com/meremblasting/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-brand-red font-bold hover:text-white transition-colors" data-id="element-186">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-187">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" data-id="element-188" />
                  </svg>
                  Message on Facebook
                </a>
              </motion.div>
            </motion.div>

            {/* Right Col: Quote Form */}
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="lg:col-span-8" data-id="element-189">
              <div className="bg-white border border-steel-200 p-6 md:p-10 shadow-sm" data-id="element-190">
                <h2 className="text-3xl font-heading font-extrabold uppercase text-charcoal mb-2" data-id="element-191">
                  Request a Free Estimate
                </h2>
                <p className="text-steel-500 mb-8" data-id="element-192">
                  Fill out the details below and we'll get back to you within
                  24-48 hours.
                </p>

                {formStatus === 'success' ? <div className="bg-steel-50 border border-green-200 p-8 text-center rounded-sm" data-id="element-193">
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" data-id="element-194" />
                    <h3 className="text-2xl font-heading font-bold text-charcoal mb-2" data-id="element-195">
                      Request Sent Successfully
                    </h3>
                    <p className="text-steel-600" data-id="element-196">
                      Thank you for reaching out. We've received your project
                      details and will be in touch shortly with your estimate.
                    </p>
                    <Button variant="primary" className="mt-8" onClick={() => setFormStatus('idle')} data-id="element-197">
                      Submit Another Request
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-198">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-199">
                      <div data-id="element-200">
                        <label htmlFor="name" className="block text-sm font-bold text-charcoal mb-2" data-id="element-201">
                          Full Name *
                        </label>
                        <input type="text" id="name" required className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="John Doe" data-id="element-202" />
                      </div>
                      <div data-id="element-203">
                        <label htmlFor="phone" className="block text-sm font-bold text-charcoal mb-2" data-id="element-204">
                          Phone Number *
                        </label>
                        <input type="tel" id="phone" required className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="(555) 123-4567" data-id="element-205" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-206">
                      <div data-id="element-207">
                        <label htmlFor="email" className="block text-sm font-bold text-charcoal mb-2" data-id="element-208">
                          Email Address
                        </label>
                        <input type="email" id="email" className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="john@example.com" data-id="element-209" />
                      </div>
                      <div data-id="element-210">
                        <label htmlFor="location" className="block text-sm font-bold text-charcoal mb-2" data-id="element-211">
                          Project City / Location *
                        </label>
                        <input type="text" id="location" required className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="e.g. Lansing, MI" data-id="element-212" />
                      </div>
                    </div>

                    <div data-id="element-213">
                      <label htmlFor="service" className="block text-sm font-bold text-charcoal mb-2" data-id="element-214">
                        Primary Service Needed
                      </label>
                      <select id="service" className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors appearance-none" data-id="element-215">
                        <option value="" data-id="element-216">Select a service...</option>
                        <option value="dustless" data-id="element-217">Dustless Blasting</option>
                        <option value="dry" data-id="element-218">Dry Blasting</option>
                        <option value="auto" data-id="element-219">Automotive Restoration</option>
                        <option value="heavy-equip" data-id="element-220">
                          Heavy Equipment Cleaning
                        </option>
                        <option value="graffiti" data-id="element-221">Graffiti Removal</option>
                        <option value="concrete" data-id="element-222">
                          Concrete/Brick Restoration
                        </option>
                        <option value="marine" data-id="element-223">Boat/Marine Prep</option>
                        <option value="other" data-id="element-224">Other / Unsure</option>
                      </select>
                    </div>

                    <div data-id="element-225">
                      <label htmlFor="message" className="block text-sm font-bold text-charcoal mb-2" data-id="element-226">
                        Project Details *
                      </label>
                      <textarea id="message" required rows={5} className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors resize-y" placeholder="Tell us about the surface, what needs to be removed, and your ideal timeline..." data-id="element-227"></textarea>
                    </div>

                    <Button type="submit" variant="primary" size="lg" className="w-full md:w-auto" disabled={formStatus === 'submitting'} data-id="element-228">
                      {formStatus === 'submitting' ? 'Sending Request...' : 'Get My Free Quote'}
                    </Button>
                    <p className="text-xs text-steel-400 mt-4" data-id="element-229">
                      By submitting this form, you agree to be contacted by
                      Merem Blasting regarding your project. We do not share
                      your information.
                    </p>
                  </form>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}